import styled, { css } from 'styled-components'
import { Row, Column, Button } from '@sainsburys-tech/bolt'
import { colorToLunaToken } from '../../../helpers/theming/utilities'
import { NectarHeading, TextWrapper } from '../../../helpers/StyledWrappers.styles'
import {
  C001StyledContainer,
  C001StyledLogoWrapper,
  C001StyledCTA,
  C001StyledTextWrapper,
  C001StyledHeading,
  C001StyledContentWrapper
} from './types'

const leftAlignedText = (props) => props.textAlignment === 'left'
const imgRatio = (ratio) => ratio.replace(':', '/')
const backgroundColor = (props) => {
  if (props.backgroundColor.includes('#')) {
    return props.backgroundColor
  }

  return colorToLunaToken(props.backgroundColor, props.theme) || 'white'
}
const linkColor = (props) => colorToLunaToken(props.linkColor, props.theme) || props.theme.color.palette['brand-1']

const containerCSS = css<C001StyledContainer>`
  text-decoration: none;
  margin-bottom: ${(props) => props.theme.size.spacing.lg};
  background: ${(props) => backgroundColor(props)};
  border-radius: ${(props) => props.theme.size.border.radius.base};
  flex-direction: ${(props) => (props.flipOrientation ? 'row' : 'row-reverse')};
  height: calc(100% - 16px);
  min-height: 200px;
  ${(props) =>
    props.theme.channel === 'sainsburys' &&
    css`
      @media screen and (max-width: ${props.theme.size.breakpoint['min-width'].md}) {
        min-height: unset;
      }
    `}

  ${(props) =>
    props.tabletImagePositionTop &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        flex-direction: column-reverse;
      }
    `}
`

export const StyledContainer = styled(Row)<C001StyledContainer>`
  ${containerCSS}
  overflow: hidden;

  ${(props) =>
    props.mobileImagePosition &&
    css`
      @media screen and (max-width: ${props.theme.size.breakpoint['max-width'].xs}) {
        flex-direction: ${props.mobileImagePosition === 'top' ? 'column-reverse' : 'column'};
      }
    `}

  ${(props) =>
    props.theme.channel === 'sainsburys' &&
    props.mobileImagePosition &&
    css`
      @media screen and (max-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        flex-direction: ${props.mobileImagePosition === 'top' ? 'column-reverse' : 'column'};
      }
    `}
`

export const StyledContainerWithLogo = styled(Row)<C001StyledContainer>`
  ${containerCSS}

  ${(props) => css`
    @media screen and (max-width: ${props.theme.size.breakpoint['max-width'].xs}) {
      flex-direction: column;
    }
  `}
`

const flexCSS = css`
  @media screen and (min-width: ${(props) => props.theme.size.breakpoint['min-width'].xs}) {
    flex: auto;
  }
`

export const StyledContentWrapper = styled(Column)<C001StyledContentWrapper>`
  display: flex;
  margin-bottom: ${(props) => props.theme.size.spacing.lg};
  ${(props) =>
    props.isCitrus &&
    css`
      margin-bottom: 0;
      background-color: ${props.theme.color.monochrome.white};
    `};

  ${(props) =>
    props.hasLogo &&
    css`
      margin-top: ${props.theme.size.spacing.md};
    `}

  ${flexCSS}
  ${(props) =>
    props.tabletImagePositionTop &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        max-width: 100%;
      }
    `}
`

const textWrapperCSS = css<C001StyledTextWrapper>`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${(props) => css`
    color: ${colorToLunaToken(props.textColor, props.theme) || props.theme.color.monochrome.darker};
    justify-content: ${props.mobileImagePosition ? 'flex-start' : 'center'};
    align-items: ${leftAlignedText(props) ? 'flex-start' : 'center'};
    @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
      justify-content: center;
    }
  `}
  ${(props) =>
    props.tabletImagePositionTop &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        justify-content: flex-start;
      }
    `}
`

export const StyledTextWrapperNoLogo = styled(Column)<C001StyledTextWrapper>`
  ${textWrapperCSS}
  ${TextWrapper}

  ${(props) => css`
    margin: ${props.theme.size.spacing.md};

    @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
      width: ${leftAlignedText(props) ? 'fit-content' : '100%'};
      margin-left: ${leftAlignedText(props) ? props.theme.size.spacing['2xl'] : 0};
    }
  `}
`

export const StyledTextWrapperLogo = styled(Column)<C001StyledTextWrapper>`
  ${textWrapperCSS}
  ${TextWrapper}
  width: auto;

  ${(props) =>
    css`
      margin: ${leftAlignedText(props) ? 'auto 8%' : 0};

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        -ms-flex-align: start;
        margin-left: 11%;
        margin-right: 17%;
        align-items: left;
      }

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) {
        margin-left: 10%;
        margin-right: 12%;
      }

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        margin-left: 11%;
        margin-right: 14%;
      }
    `}
`

export const StyledLogoWrapper = styled.div<C001StyledLogoWrapper>`
  height: 0;
  width: 100%;
  padding-bottom: 14%;
  margin-bottom: 10px;
  background-size: contain;
  background-repeat: no-repeat;

  ${(props) => css`
    background-position: ${leftAlignedText(props) ? 'left' : 'center'};
    background-image: url(${props.logo});

    @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) {
      padding-bottom: 16%;
    }

    @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
      padding-bottom: 12%;
    }
  `}
`

export const StyledHeading = styled.h1<C001StyledHeading>`
  ${NectarHeading}
  ${(props) =>
    props.isOffer &&
    css`
      color: ${props.theme.color.content.sale};
    `};
`
export const StyledCTA = styled(Button)<C001StyledCTA>`
  ${(props) =>
    props.styleCTA === 'link' &&
    css`
      color: ${linkColor(props)};
    `}
`

export const StyledImageWrapper = styled(Column)`
  display: flex;
  aspect-ratio: ${(props) => imgRatio(props.tabletImagePositionTop ? props.ratioInGrid : props.imageRatio)};
  position: relative;

  ${flexCSS}

  @media screen and (max-width: ${(props) => props.theme.size.breakpoint['max-width'].xs}) {
    aspect-ratio: ${(props) => (props.mobileImagePosition ? 3 / 2 : 4 / 5)};
  }

  ${(props) =>
    props.xs === 12 &&
    css`
      @media screen and (max-width: ${props.theme.size.breakpoint['max-width'].xs}) {
        flex-basis: unset;
      }
    `}

  picture {
    width: 100%;
    ${(props) =>
      props.theme.channel === 'sainsburys' &&
      css`
        display: flex;
        justify-content: center;
        overflow: hidden;
      `}
  }

  img {
    height: 100%;
    /* This is to override the Luna styles */
    ${(props) =>
      props.theme.channel === 'sainsburys' &&
      css`
        max-width: unset;
      `}
  }
  ${(props) =>
    props.tabletImagePositionTop &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        aspect-ratio: ${imgRatio(props.imageRatio)};
        max-width: 100%;
        flex-basis: unset;
      }
    `}
`
